import { useState } from "react";
import { categories, featuredItems } from "@/mocks/heigua";
import SectionHeading from "./SectionHeading";

export default function ArchiveLibrary() {
  const [active, setActive] = useState("all");
  const [keyword, setKeyword] = useState("");

  const list = featuredItems.filter((item) => {
    const matchCat = active === "all" || item.category === active;
    const kw = keyword.trim().toLowerCase();
    const matchKw =
      !kw ||
      item.title.toLowerCase().includes(kw) ||
      item.excerpt.toLowerCase().includes(kw) ||
      item.tag.toLowerCase().includes(kw) ||
      item.category.toLowerCase().includes(kw);
    return matchCat && matchKw;
  });

  return (
    <section id="library" className="bg-background-50 py-14 md:py-20">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <SectionHeading
          index="壹"
          eyebrow="Archive Index"
          title="档案检索 · 旧料精选"
          subtitle="用关键词直接检索，或按分类筛选。想补上前面的瓜，从这一栏开始翻准没错。"
        />

        {/* 搜索 + 分类 */}
        <div className="mt-8 flex flex-col gap-4">
          <div className="relative w-full max-w-xl">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-foreground-400" />
            <input
              type="search"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="搜索标题、标签或摘要，例如：综艺 / 翻红 / 反转…"
              className="w-full rounded-md border border-background-300 bg-background-50 py-3 pl-11 pr-4 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="flex flex-wrap gap-2" role="tablist" aria-label="分类筛选">
            {categories.map((c) => {
              const isActive = active === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActive(c.key)}
                  className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3.5 py-1.5 text-sm transition-colors ${
                    isActive
                      ? "bg-primary-500 text-background-50"
                      : "border border-background-200 bg-background-100 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
                  }`}
                >
                  <i className={c.icon} />
                  {c.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* 结果 */}
        {list.length === 0 ? (
          <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 py-16 text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-full bg-background-200 text-foreground-500">
              <i className="ri-inbox-archive-line text-2xl" />
            </span>
            <p className="mt-4 text-base font-medium text-foreground-800">没有检索到相关旧料</p>
            <p className="mt-1 text-sm text-foreground-500">
              换个关键词，或清空分类筛选再看看
            </p>
            <button
              type="button"
              onClick={() => {
                setKeyword("");
                setActive("all");
              }}
              className="mt-5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm text-foreground-700 hover:border-primary-400 hover:text-primary-700"
            >
              重置筛选
            </button>
          </div>
        ) : (
          <div id="featured" className="mt-8 grid scroll-mt-24 grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {list.map((item, i) => (
              <article
                key={item.id}
                className="group flex flex-col overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-300"
              >
                <div className="relative aspect-[3/2] overflow-hidden bg-background-200">
                  <img
                    src={item.image}
                    alt={`${item.title}的旧料档案配图`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <span className="absolute left-3 top-3 inline-flex items-center gap-1 rounded-full bg-primary-500/90 px-2.5 py-1 text-[11px] text-background-50">
                    <i className={categories.find((c) => c.label === item.category)?.icon ?? "ri-price-tag-3-line"} />
                    {item.category}
                  </span>
                  <span className="absolute right-3 top-3 rounded-full bg-background-50/90 px-2.5 py-1 text-[11px] text-foreground-600">
                    {item.year}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4 md:p-5">
                  <div className="flex items-center gap-2 text-[11px] text-foreground-500">
                    <span className="inline-flex items-center gap-1 rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">
                      <i className="ri-price-tag-3-line" />
                      {item.tag}
                    </span>
                    <span>{item.tone}</span>
                  </div>
                  <h3 className="mt-2.5 font-heading text-base font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-lg">
                    <a href="#deep">{item.title}</a>
                  </h3>
                  <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
                    {item.excerpt}
                  </p>
                  <div className="mt-auto flex items-center justify-between pt-4">
                    <span className="font-heading text-xs text-foreground-400">
                      {String(i + 1).padStart(2, "0")} · 黑瓜档案
                    </span>
                    <a
                      href="#deep"
                      className="inline-flex items-center gap-1 whitespace-nowrap text-sm font-medium text-primary-600 hover:text-primary-700"
                    >
                      打开档案
                      <i className="ri-arrow-right-line transition-transform group-hover:translate-x-0.5" />
                    </a>
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}